import { uniq, uniqBy } from 'lodash';
import { ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY } from 'utils/organization';

import type { VideoTutorialEntry } from '../types/VideoTutorialEntry.type';

export const VideoKind = {
  CREATE_OFFER: 'create_your_offer',
  EDIT_OFFER: 'edit_an_existing_offer',
  BULK_SHORTLIST_BULK_OFFER: 'bulk_shortlist_and_bulk_offer',
  BULK_MESSAGE_BULK_OFFER: 'bulk_message_and_bulk_edit',
  MANAGE_MULTI_BRAND: 'manage_multiple_brands_overview',
  SHARE_INFORMATION_TEAM: 'share_information_with_your_team',
  MANAGE_SETTINGS: 'manage_your_email_settings'
};

export const OFFERS_REGEXP = [
  /\/campaigns\/\d+\/offers/i,
  /\/campaigns\/\d+\/subcampaigns\/\d+$/i
];

export const SPECIFY_OFFER_REGEXP = [
  /\/campaigns\/\d+\/subcampaigns\/\d+\/edit\?new/i
];

export const EDIT_OFFER_REGEXP = [
  /\/campaigns\/\d+\/subcampaigns\/\d+\/edit\?existing/i
];

export const PARTICIPANT_REGEXP = [
  /\/campaigns\/\d+\/participants/i
];

export const MESSAGES_REGEXP = [
  /\/campaigns\/\d+\/messages/i
];

export const AGENCY_HOME_REGEXP = [
  /\/agency\/\d+\/overview/i
];

export const SETTINGS_REGEXP = [
  /\/user\/settings/i
];

export const videoTutorialList: Array<VideoTutorialEntry> = [
  // 1.
  {
    id: 'TVSJ0ziQdG8',
    kind: VideoKind.CREATE_OFFER,
    name: 'Create your offer',
    pageRegExp: [
      ...SPECIFY_OFFER_REGEXP,
      /\/campaigns\/\d+\/participants\/shortlisted/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY]
  },
  {
    id: 'TVSJ0ziQdG8',
    kind: VideoKind.CREATE_OFFER,
    name: 'Create your offer',
    pageRegExp: [
      ...OFFERS_REGEXP
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlySelfService: true
  },

  // 2.
  {
    id: 'uE_CquCmj3U',
    kind: VideoKind.EDIT_OFFER,
    name: 'Edit an existing offer',
    pageRegExp: [
      ...EDIT_OFFER_REGEXP,
      ...OFFERS_REGEXP,
      /\/campaigns\/\d+\/participants\/pending/i,
      /\/campaigns\/\d+\/participants\/agreed/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlyManagedService: true
  },
  {
    id: 'uE_CquCmj3U',
    kind: VideoKind.EDIT_OFFER,
    name: 'Edit an existing offer',
    pageRegExp: [
      ...PARTICIPANT_REGEXP,
      ...OFFERS_REGEXP
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlySelfService: true
  },

  // 3.
  {
    id: 'g-XBClkYhl4',
    kind: VideoKind.BULK_SHORTLIST_BULK_OFFER,
    name: 'Bulk shortlist and bulk offer',
    pageRegExp: [
      ...EDIT_OFFER_REGEXP,
      ...SPECIFY_OFFER_REGEXP,
      ...OFFERS_REGEXP,
      /\/collections\/creators/i,
      /\/collections\/creators\/\d+/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY]
  },
  {
    id: 'g-XBClkYhl4',
    kind: VideoKind.BULK_SHORTLIST_BULK_OFFER,
    name: 'Bulk shortlist and bulk offer',
    pageRegExp: [
      ...EDIT_OFFER_REGEXP,
      ...SPECIFY_OFFER_REGEXP,
      ...OFFERS_REGEXP,
      /\/campaigns\/\d+\/participants\/shortlisted/i,
      /\/collections\/creators/i,
      /\/collections\/creators\/\d+/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlyManagedService: true
  },

  // 4.
  {
    id: 'TmxAXRP-bvM',
    kind: VideoKind.BULK_MESSAGE_BULK_OFFER,
    name: 'Bulk message and bulk edit',
    pageRegExp: [
      ...OFFERS_REGEXP,
      ...EDIT_OFFER_REGEXP,
      ...SPECIFY_OFFER_REGEXP,
      /\/collections\/creators$/i,
      /\/campaigns\/\d+\/participants\/pending/i,
      /\/campaigns\/\d+\/participants\/agreed/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlyManagedService: true
  },
  {
    id: 'TmxAXRP-bvM',
    kind: VideoKind.BULK_MESSAGE_BULK_OFFER,
    name: 'Bulk message and bulk edit',
    pageRegExp: [
      ...MESSAGES_REGEXP
    ],
    userType: [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY],
    onlySelfService: true
  },

  // 5.
  {
    id: 'ZlEXh4oKlA0',
    kind: VideoKind.MANAGE_MULTI_BRAND,
    name: 'Manage multiple brands',
    pageRegExp: [
      ...AGENCY_HOME_REGEXP
    ],
    userType: [ORGANIZATION_TYPE_BRAND_AGENCY]
  },

  // 6.
  {
    id: 'Sxx3oWpGsHM',
    kind: VideoKind.SHARE_INFORMATION_TEAM,
    name: 'Share details with your team',
    pageRegExp: [
      /\/collections\/creators$/i,
      ...AGENCY_HOME_REGEXP,
      /\/discovery\/creators/i,
      /\/content-creator\/profiles\/\d+/i
    ],
    userType: [ORGANIZATION_TYPE_BRAND_AGENCY],
    onlySelfService: true
  },

  // 7.
  {
    id: 'nCv8XkoyK58',
    kind: VideoKind.MANAGE_SETTINGS,
    name: 'Manage your email settings',
    pageRegExp: [
      ...SETTINGS_REGEXP
    ],
    userType: [ORGANIZATION_TYPE_BRAND_AGENCY]
  }
];

export const distinctVideoTutorialList: Array<VideoTutorialEntry> = uniqBy(videoTutorialList, 'id');

export function getVideosForCurrentUser(organizationType?: string | null, isSelfService?: boolean | null): Array<VideoTutorialEntry> {
  return videoTutorialList
    .filter((video) => {
      if (!organizationType || !video.userType.includes(organizationType)) {
        return false;
      }

      const forEveryone = !video.onlyManagedService && !video.onlySelfService;
      const forManaged = video.onlyManagedService && !isSelfService;
      const forSelf = video.onlySelfService && isSelfService;

      return forManaged || forSelf || forEveryone;
    });
}

export function getDistinctVideoIds(videos: Array<VideoTutorialEntry>): Array<string> {
  return uniq(videos.map(video => video.id));
}

export function getActivePageVideos(path: string, pageVideos: Array<VideoTutorialEntry>): Array<VideoTutorialEntry> {
  return pageVideos
    .filter((video) => video.pageRegExp.some((regex) => regex.test(path)));
}
